////Users/bobbygilbert/Documents/GitHub/TPCTrust-platform/app/[locale]/(routes)/crm/opportunities/table-componentsB/data-table.tsx

/* eslint-disable react-hooks/rules-of-hooks */
"use client";


import * as React from "react";
import { useRouter } from "next/navigation";
import axios from "axios";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { DataTableToolbar } from "./data-table-toolbar";
import { PanelTopClose, PanelTopOpen, Edit } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from "@radix-ui/react-icons";
import Papa from "papaparse"; // Import papaparse for CSV export
import { CommentModal } from "./comment-modal";

// Define a more specific type for our opportunity data
interface OpportunityData {
  id: string;
  createdAt?: Date | string;
  created_on?: Date | string;
  name?: string;
  budget?: number;
  expected_revenue?: number;
  account?: string;
  sales_stage?: string;
  sales_stage2?: string;
  sales_stage3?: string;
  comments?: string | null;
  assigned_account?: {
    name?: string;
  };
  assigned_leads?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    advisorName?: string | null;
    advisorEmail?: string | null;
    state?: string | null;
    assigned_to_user?: {
      name?: string | null;
    } | null;
  } | null;
  [key: string]: any; // For any other properties
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

export function OpportunitiesDataTable<TData, TValue>({
  columns,
  data,
}: DataTableProps<TData, TValue>) {
  const router = useRouter();
  const { toast } = useToast();
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [search, setSearch] = React.useState(""); 
  const [hide, setHide] = React.useState(false);
  const [selectedYear, setSelectedYear] = React.useState("2025"); // Default to 2025
  const [refreshKey, setRefreshKey] = React.useState(0);

  // Function to refresh data - this will be passed to the comment modal
  const refreshData = async () => {
    try {
      setRefreshKey(prevKey => prevKey + 1);
      router.refresh();
      
      toast({
        title: "Success",
        description: "Comments updated successfully",
      });
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast({
        title: "Error",
        description: "Failed to refresh data",
        variant: "destructive",
      });
    }
  };

  // Add comments column to the columns
  const columnsWithComments = React.useMemo(() => {
    // Check if comments column already exists to avoid duplicates
    const hasCommentsColumn = columns.some((col) => 
      'accessorKey' in col && col.accessorKey === "comments" || 
      'id' in col && col.id === "comments"
    );

    if (hasCommentsColumn) {
      return columns;
    }

    // Add comments column
    return [
      ...columns,
      {
        id: "comments",
        header: "Comments",
        cell: ({ row }) => {
          const [showModal, setShowModal] = React.useState(false);
          const opportunity = row.original as OpportunityData;
          
          return (
            <div className="flex items-center gap-2">
              <div className="max-w-[200px] truncate">
                {opportunity.comments || "No comments yet"}
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setShowModal(true)}
                className="w-8 h-8"
              >
                <Edit className="w-4 h-4" />
              </Button>
              
              {showModal && (
                <CommentModal
                  isOpen={showModal}
                  onClose={() => setShowModal(false)}
                  opportunityId={opportunity.id}
                  currentComment={opportunity.comments || null} // Convert undefined to null
                  onSuccess={refreshData}
                />
              )}
            </div>
          );
        },
        enableSorting: true,
        enableHiding: true,
      },
    ];
  }, [columns, refreshData]);

  // Filter data by year
  const filteredData = React.useMemo(() => {
    return (data as OpportunityData[]).filter((item) => {
      const itemDate = item.createdAt || item.created_on;
      if (!itemDate) return true; // Keep items without dates
      
      const createdYear = new Date(itemDate).getFullYear().toString();
      return createdYear === selectedYear;
    });
  }, [data, selectedYear, refreshKey]);

  const table = useReactTable({
    data: filteredData as TData[],
    columns: columnsWithComments as ColumnDef<TData, TValue>[],
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  // Search handler
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    
    // Apply filter to name field
    setColumnFilters([
      { id: "name", value }
    ]);
  };

  // Mapping of account codes to names
  const accountMapping: { [key: string]: string } = {
    clu7nn3in00067p77cwuq0qbd: "Bevel",
    clu7nocab00087p77ul59mab4: "Craft Capital",
    cluqzlo7f0001qgkks8v16kuc: "Other Financing",
    cluqzn7yz0003qgkkvtb1fsbj: "Cash",
    // Add more mappings as needed
  };

  const salesStageMapping: { [key: string]: string } = {
    "clu7mz9fh000fijpzzcr2o7wo": "Documents Received",
    "clu7mz9fh000xijpzbackburn": "Backburner",
    "clu7mz9fh000gijpz7n2n93rq": "In Financing",
    "clu7mz9fh000hijpz7qkygf03": "Pending Equipment",
    "clu7mz9fh000xijpzprior123": "Priority",
    "clu7mz9fh000iijpzh5hz7oh5": "SO Created",
    "clu7mz9fh000jijpzlp2le32w": "Payment pending",
    "clu7mz9fh000kijpztf43pmeb": "Promissory note completed",
    "clu7mz9fh000lijpzkxsroj0d": "Deposit and platform fee paid",
    "clu7mz9fh000mijpz0l2pwqzt": "Sale confirmed",
    "clu7mz9fh000nijpz0lv0eou5": "Sign re-purchase agreement"
  };

  // Mapping of sales_stage2 codes to names
  const salesStage2Mapping: { [key: string]: string } = {
    clu7mz9rh000oijpz2pebrfch: "Deal Reviewed by TPC",
    clu7mz9rh000pijpzkg5zi7zg: "Finance Partners Initial Review and Assess",
    cltwi0l9w000qo4xzi3rj6brp: "Refer to Lender",
    cltwi0l9w000ro4xz9n1m2kgp: "Lenders Initial Review and Assessments",
    cltwi0l9w000so4xzhckmvm8t: "Accepted",
    cltwi0l9w000to4xzflrwkuqn: "Declined",
    cltwi0l9w000uo4xzoaz8yrft: "Additional Information Needed",
    cltwi0l9w000vo4xzincbm7va: "Term Sheet Offered",
    cltwi0l9w000wo4xzj8vw4h1k: "Term Sheet Accepted",
  };

  // Mapping of sales_stage3 codes to names
  const salesStage3Mapping: { [key: string]: string } = {
    clu7mza3d0014jjpzne89xep1: "Yet to Start",
    clu7mza3d0015ijpzk3cv1adj: "Term Sheet Accepted",
    clu7mza3d0010ijpz0eybj2rr: "Lending Partner Need from Client? All Partners",
    clu7mza3d00zjjpzgpbwjiy8z: "What Bank is Loan At?",
    clu7mza3d000xijpzxe58l3ni: "Bridge Docs – Signed",
    clu7mza3d000yjjpzu72gr34y: "EZ Side Complete",
  };

  // Utility functions for formatting
  const formatDate = (dateString: string | Date | null | undefined) => {
    if (!dateString) return "N/A";
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatCurrency = (amount: number | string | null | undefined) => {
    if (amount === null || amount === undefined) return "N/A";
    // Ensure the amount is a number
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
    }).format(numAmount);
  };

  // Mapping function for export
  const mapDataForExport = (data: OpportunityData[]) => {
    return data.map((row) => ({
      Created: formatDate(row.createdAt || row.created_on),
      Name: row.name,
      Account: accountMapping[row.account || ""] || 
               (row.assigned_account?.name || "N/A"),
      Amount: formatCurrency(row.budget || row.expected_revenue),
      "Equipment Financing and Sale Process Pipeline":
        salesStageMapping[row.sales_stage || ""] || "Yet to Start",
      "Deal Review and Lending Process Pipeline":
        salesStage2Mapping[row.sales_stage2 || ""] || "Yet to Start",
      "Bridge Note Pipeline":
        salesStage3Mapping[row.sales_stage3 || ""] || "Yet to Start",
      "Advisor": row.assigned_leads?.advisorName || 
                (row.assigned_leads?.assigned_to_user?.name || "N/A"),
      "Investor": row.assigned_leads ? 
                `${row.assigned_leads.firstName || ""} ${row.assigned_leads.lastName || ""}`.trim() || "N/A" : 
                "N/A",
      "State": row.assigned_leads?.state || "N/A",
      "Comments": row.comments || ""
    }));
  };

  // CSV Export Function
  const handleExport = () => {
    // Use filtered & sorted data for export
    const exportData = mapDataForExport(
      table.getFilteredRowModel().rows.map(row => row.original as OpportunityData)
    );

    // Define CSV headers with renamed titles
    const csvHeaders = [
      "Created",
      "Name",
      "Account",
      "Amount",
      "Equipment Financing and Sale Process Pipeline",
      "Deal Review and Lending Process Pipeline",
      "Bridge Note Pipeline",
      "Advisor",
      "Investor",
      "State",
      "Comments"
    ];

    // Convert to CSV with headers
    const csv = Papa.unparse({
      fields: csvHeaders,
      data: exportData.map((row) => [
        row.Created,
        row.Name,
        row.Account,
        row.Amount,
        row["Equipment Financing and Sale Process Pipeline"],
        row["Deal Review and Lending Process Pipeline"],
        row["Bridge Note Pipeline"],
        row["Advisor"],
        row["Investor"],
        row["State"],
        row["Comments"]
      ]),
    });

    // Create a blob and trigger download
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `craft_capital_opportunities_${selectedYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Custom DataTablePagination component inline
  function DataTablePagination() {
    return (
      <div className="flex items-center justify-between px-2">
        <div className="flex-1 text-sm text-muted-foreground">
          {table.getFilteredSelectedRowModel().rows.length} of{" "}
          {table.getFilteredRowModel().rows.length} row(s) selected.
        </div>
        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => {
                if (value === "all") {
                  // Set the page size to the total number of rows
                  table.setPageSize(table.getFilteredRowModel().rows.length);
                } else {
                  table.setPageSize(Number(value));
                }
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={table.getState().pagination.pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-[100px] items-center justify-center text-sm font-medium">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="hidden w-8 h-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Go to first page</span>
              <DoubleArrowLeftIcon className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              className="w-8 h-8 p-0"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeftIcon className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              className="w-8 h-8 p-0"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRightIcon className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              className="hidden w-8 h-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">Go to last page</span>
              <DoubleArrowRightIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {/* Search input */}
          <Input
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder="Search by name..."
            className="max-w-sm"
          />
          
          {/* Year filter dropdown */}
          <Select
            value={selectedYear}
            onValueChange={setSelectedYear}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Select Year" />
            </SelectTrigger>
            <SelectContent side="top">
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex justify-end space-x-2">
          {/* Export Button */}
          <Button onClick={handleExport} variant="secondary">
            Export CSV
          </Button>
          
          {hide ? (
            <PanelTopOpen
              onClick={() => setHide(!hide)}
              className="text-muted-foreground"
            />
          ) : (
            <PanelTopClose
              onClick={() => setHide(!hide)}
              className="text-muted-foreground"
            />
          )}
        </div>
      </div>

      {hide ? (
        <div className="flex gap-2">
          This content is hidden now. Click on <PanelTopOpen /> to show content
        </div>
      ) : (
        <>
          <DataTableToolbar table={table} />
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <DataTablePagination />
        </>
      )}
    </div>
  );
}